import { z } from 'zod';

import {
  AgentRoleSchema,
  GlobalRoleSchema,
  IsoDateTimeSchema,
  UserKindSchema,
  UuidSchema,
} from './common.js';

export const UserRefSchema = z.object({
  id: UuidSchema,
  login: z.string(),
  displayName: z.string(),
  avatarColor: z.string(),
  /** Абсолютный путь к загруженному фото; `null` — инициалы на `avatarColor`. */
  avatarUrl: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  /** Тип участника: нужен выбору владельца API-токена (E21-T1). */
  kind: UserKindSchema.optional(),
  agentRole: AgentRoleSchema.nullable().optional(),
});
export type UserRef = z.infer<typeof UserRefSchema>;

export const UserSchema = z.object({
  id: UuidSchema,
  login: z.string(),
  /** `null` у AI агента: почты у него нет (docs/03 §1). */
  email: z.string().email().nullable(),
  displayName: z.string(),
  role: GlobalRoleSchema,
  /** Тип пользователя: человек или AI агент (UC-USR-08). */
  kind: UserKindSchema,
  /** Специализация агента; у человека `null`. */
  agentRole: AgentRoleSchema.nullable(),
  /** Общий агент на несколько проектов (docs/17, docs/18). */
  isSharedAgent: z.boolean().optional(),
  /** Root-доступ к серверу, где работает агент; пароль не возвращается. */
  agentServerHost: z.string().nullable(),
  hasAgentServerPassword: z.boolean(),
  isActive: z.boolean(),
  mustChangePassword: z.boolean(),
  /** Заполнен, пока пользователь не завершил регистрацию по приглашению. */
  invitedAt: IsoDateTimeSchema.nullable(),
  /**
   * Статус приглашения: есть только в админском списке (UC-USR-07), в `/me`
   * и ответе логина — `null`.
   */
  invitationStatus: z.enum(['pending', 'expired', 'accepted', 'revoked']).nullable().optional(),
  /**
   * Последнее приглашение: нужен списку админки для «Отправить повторно» и
   * «Отозвать» (UC-USR-07 AC1).
   */
  invitationId: UuidSchema.nullable().optional(),
  avatarColor: z.string(),
  /** `/api/users/:id/avatar?v=…`; `null` — фото не загружено. */
  avatarUrl: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type User = z.infer<typeof UserSchema>;

export const LoginBodySchema = z
  .object({
    login: z.string().min(1, 'Введите логин').max(64),
    password: z.string().min(1, 'Введите пароль').max(200),
  })
  .strict();
export type LoginBody = z.infer<typeof LoginBodySchema>;

export const LoginResponseSchema = z.object({
  user: UserSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const MeResponseSchema = z.object({
  user: UserSchema,
  projects: z.array(
    z.object({
      id: UuidSchema,
      key: z.string(),
      name: z.string(),
      role: z.enum(['project_admin', 'member']),
    }),
  ),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const CreateUserBodySchema = z
  .object({
    login: z
      .string()
      .regex(/^[a-z0-9][a-z0-9._-]{2,31}$/, 'Логин: 3–32 символа, строчные латинские, цифры, . _ -'),
    email: z.string().email('Некорректный email'),
    displayName: z.string().min(1, 'Введите имя').max(80),
    role: GlobalRoleSchema.default('member'),
    password: z.string().min(8, 'Пароль не короче 8 символов').max(200),
  })
  .strict();
export type CreateUserBody = z.infer<typeof CreateUserBodySchema>;

export const UpdateUserBodySchema = z
  .object({
    email: z.string().email().optional(),
    displayName: z.string().min(1).max(80).optional(),
    role: GlobalRoleSchema.optional(),
    isActive: z.boolean().optional(),
    password: z.string().min(8).max(200).optional(),
  })
  .strict();
export type UpdateUserBody = z.infer<typeof UpdateUserBodySchema>;

export const ChangePasswordBodySchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8, 'Пароль не короче 8 символов').max(200),
  })
  .strict();
export type ChangePasswordBody = z.infer<typeof ChangePasswordBodySchema>;

export const ListUsersQuerySchema = z.object({
  q: z.string().max(100).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('all'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListUsersQuery = z.infer<typeof ListUsersQuerySchema>;

export const AUDIT_ACTIONS = [
  'auth.login',
  'auth.logout',
  'auth.login_failed',
  'auth.password_changed',
  'user.created',
  'user.updated',
  'user.avatar_updated',
  'user.avatar_removed',
  'user.deactivated',
  'user.activated',
  'user.registered',
  'invitation.sent',
  'invitation.resent',
  'invitation.revoked',
  'mail.settings_updated',
  'mail.test_sent',
  'project.created',
  'project.updated',
  'project.archived',
  'project.unarchived',
  'project.deleted',
  'member.added',
  'member.updated',
  'member.removed',
  'status.created',
  'status.updated',
  'status.deleted',
  'status.reordered',
  'transition.created',
  'transition.updated',
  'transition.deleted',
  'issue.created',
  'issue.updated',
  'issue.deleted',
  'issue.transitioned',
  'issue.ranked',
  'issue.duplicated',
  'comment.created',
  'comment.updated',
  'comment.deleted',
  'attachment.uploaded',
  'attachment.deleted',
  'sprint.created',
  'sprint.updated',
  'sprint.started',
  'sprint.completed',
  'sprint.deleted',
  'filter.created',
  'filter.updated',
  'filter.deleted',
] as const;

export const AuditEntrySchema = z.object({
  id: z.number().int(),
  actor: UserRefSchema.nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  payload: z.record(z.unknown()),
  ip: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;
